import React, { useState, useEffect, useCallback } from 'react';
import { Flag, Eye, ThumbsUp, ThumbsDown, CheckCircle, Clock, FileWarning } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { useAdminSettings } from '../../../hooks/useAdminSettings';

const ContentModerationTab = () => {
  const { apiCall, loading, setLoading, showSuccess, showError } = useAdminSettings();
  const [moderationQueue, setModerationQueue] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [moderationFilter, setModerationFilter] = useState('pending');

  const fetchModerationQueue = useCallback(async () => {
    try {
      const data = await apiCall('get', `/api/admin/settings/moderation/queue?status=${moderationFilter}`);
      setModerationQueue(data.content || []);
    } catch (error) {
      console.error('Error fetching moderation queue:', error);
    }
  }, [apiCall, moderationFilter]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/admin/settings/audit-logs?limit=20');
      setAuditLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchModerationQueue();
    fetchAuditLogs();
  }, [fetchModerationQueue, fetchAuditLogs]);

  const handleModerateContent = async (contentId, action, reason = '') => {
    setLoading(true);
    try {
      await apiCall('post', `/api/admin/settings/moderation/content/${contentId}`, { action, reason });
      showSuccess(`Content ${action} successfully`);
      fetchModerationQueue();
    } catch (error) {
      showError('Failed to moderate content');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Flag className="w-5 h-5 text-red-400" />
            Content Moderation Queue
          </CardTitle>
          <CardDescription className="text-gray-400">
            Review and moderate user-uploaded content
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filter Tabs */}
          <div className="flex gap-2 mb-6">
            {['pending', 'flagged', 'approved', 'rejected'].map((status) => (
              <Button
                key={status}
                onClick={() => setModerationFilter(status)}
                variant={moderationFilter === status ? 'default' : 'outline'}
                size="sm"
                className={moderationFilter === status 
                  ? 'bg-yellow-500 text-black' 
                  : 'border-gray-600 text-gray-300'}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>

          {/* Content List */}
          <div className="space-y-4">
            {moderationQueue.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p>No content in {moderationFilter} queue</p>
              </div>
            ) : (
              moderationQueue.map((content) => (
                <div key={content.id} className="flex flex-col md:flex-row gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  {/* Thumbnail */}
                  <div className="w-full md:w-32 h-24 bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
                    {content.thumbnail_url ? (
                      <img src={content.thumbnail_url} alt={content.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <FileWarning className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  
                  {/* Content Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-white">{content.title || 'Untitled'}</h4>
                      <Badge className={
                        content.moderation_status === 'approved' ? 'bg-green-500/20 text-green-400' :
                        content.moderation_status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        content.moderation_status === 'flagged' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-gray-600 text-gray-300'
                      }>
                        {content.moderation_status || 'pending'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-400">
                      By: {content.creator?.username || 'Unknown'} • {content.content_type || 'video'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Uploaded: {new Date(content.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={() => window.open(content.file_path || content.thumbnail_url, '_blank')}
                      size="sm"
                      variant="outline"
                      className="border-gray-600"
                    >
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                    <Button
                      onClick={() => handleModerateContent(content.id, 'approved')}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <ThumbsUp className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button
                      onClick={() => {
                        const reason = prompt('Enter rejection reason:');
                        if (reason) handleModerateContent(content.id, 'rejected', reason);
                      }}
                      size="sm"
                      variant="outline"
                      className="border-red-500 text-red-400 hover:bg-red-500/10"
                    >
                      <ThumbsDown className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    <Button
                      onClick={() => handleModerateContent(content.id, 'flagged')}
                      size="sm"
                      variant="outline"
                      className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/10"
                    >
                      <Flag className="w-4 h-4 mr-1" /> Flag
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Preview */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            Recent Moderation Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {auditLogs.filter(log => log.action?.includes('MODERATE')).slice(0, 10).map((log, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-gray-800/30 rounded">
                <div className="flex items-center gap-2">
                  <Badge className="bg-gray-700">{log.action}</Badge>
                  <span className="text-sm text-gray-400">by {log.admin_username}</span>
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(log.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
            {auditLogs.filter(log => log.action?.includes('MODERATE')).length === 0 && (
              <p className="text-gray-400 text-center py-4">No moderation activity yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContentModerationTab;
